import './TableHelpButton.scss';
import { FC, useContext, useMemo, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import clsx from 'clsx';
import { HiXMark } from 'react-icons/hi2';
import { MdHelpOutline } from 'react-icons/md';
import AnchoredDialog from '@srm/ui/anchored-dialog';
import AutoLinkText from '@srm/ui/auto-link-text';
import { DTB_POPUP_Z } from '@srm/ui/data-table/popup-z-index';
import { decodeHtmlEntities } from '@srm/ui/lib/html';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';
import { sanitizeDocumentHtml } from '@/utils/sanitizeDocumentHtml';

export interface ITableHelpButtonProps {
    /** Текст справки: URL становятся ссылками, разметка конфигурации выводится очищенной — как у подсказок полей. */
    help: string;
    /** Имя таблицы для подписи кнопки и панели. */
    tableName?: string;
    /** `toolbar` — квадрат туллбара таблицы; `caption` — значок у заголовка компактной подтаблицы (как у полей). */
    placement?: 'toolbar' | 'caption';
}

/** Справка таблицы — кнопка «?» в правой служебной группе туллбара. Открывается щелчком и держится, пока её
 *  не закроют: длинный текст и ссылки читаются спокойно, панель не пропадает при уводе курсора. */
const TableHelpButton: FC<ITableHelpButtonProps> = ({ help, tableName, placement = 'toolbar' }) => {
    const [opened, setOpened] = useState(false);
    const runtime = useContext(DocumentRuntimeContext);
    const readOnlyPreview = runtime.mode === 'supportPreview';
    const html = useMemo(() => {
        const markup = renderToStaticMarkup(<AutoLinkText text={help} disableLinks={readOnlyPreview} />);
        return sanitizeDocumentHtml(decodeHtmlEntities(markup), readOnlyPreview);
    }, [help, readOnlyPreview]);
    const label = tableName ? `Справка: ${tableName}` : 'Справка по таблице';
    const close = () => setOpened(false);

    return (
        <AnchoredDialog
            opened={opened}
            onClose={close}
            // в туллбаре кнопка стоит в правой служебной группе — панель раскрывается влево от неё
            align={placement === 'caption' ? 'left' : 'right'}
            width={380}
            zIndex={DTB_POPUP_Z}
            className="tdn-help"
            contentClassName="tdn-help__content"
            dropdownAria={{ 'aria-label': label }}
            data-testid="table-next-help-panel"
            target={
                <button
                    type="button"
                    className={clsx(
                        placement === 'caption' ? 'tdn-help__inline' : 'dtb__icon-btn',
                        opened && 'is-active',
                    )}
                    // у открытой панели своя шапка — подсказка над ней лишняя
                    title={opened ? undefined : label}
                    aria-label={label}
                    aria-expanded={opened}
                    onClick={() => setOpened((value) => !value)}
                    data-testid="table-next-help"
                >
                    <MdHelpOutline aria-hidden />
                </button>
            }
        >
            <div className="tdn-help__head">
                <MdHelpOutline className="tdn-help__glyph" aria-hidden />
                <div className="tdn-help__title" aria-hidden>
                    {tableName ? (
                        <>
                            <span className="tdn-help__kind">Справка</span>
                            <span className="tdn-help__name">{tableName}</span>
                        </>
                    ) : (
                        <span className="tdn-help__kind">Справка по таблице</span>
                    )}
                </div>
                <button type="button" className="tdn-help__close" aria-label="Закрыть справку" onClick={close}>
                    <HiXMark aria-hidden />
                </button>
            </div>
            {/* фокус открытия — на текст, а не на первую ссылку: стрелки и PageDown листают длинную справку */}
            <div className="tdn-help__text" tabIndex={-1} data-autofocus dangerouslySetInnerHTML={{ __html: html }} />
        </AnchoredDialog>
    );
};

export default TableHelpButton;
